import React, { useMemo } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { CommunityType } from 'types/communities';
import { SnapshotDataType } from 'types/snapshot';

import SnapshotDetails from './SnapshotDetails';
import { useCommunityViewContext } from '../../../CommunityViewContext';
import { useResolveQuarterSnapshots } from '../customHooks';
import { useDataQuarterFilterContext } from '../DataQuarterFilterContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
  overflow-y: auto;
`;

type CompetitorEntry = {
  competitor: CommunityType;
  snapshot: SnapshotDataType | null;
  label?: string;
  noDataAvailable: boolean;
};

const CompetitorListSection = () => {
  const { form, quarters } = useDataQuarterFilterContext();
  const includePriorCompetitors = Form.useWatch('includePriorCompetitors', form) ?? true;
  const selectedQuarterSlug = Form.useWatch('selectedQuarterSlug', form) ?? quarters[0]?.slug ?? null;
  const { competitors } = useCommunityViewContext();
  const { competitorMatches } = useResolveQuarterSnapshots({
    quarters,
    selectedQuarterSlug,
    competitors,
  });

  const sortedCompetitors = competitors?.length
    ? [...competitors].sort((left, right) => (left?.comunity_name || '').localeCompare(right?.comunity_name || ''))
    : [];

  const competitorEntries: (CompetitorEntry | null)[] = sortedCompetitors.length
    ? sortedCompetitors.map((competitor) => {
        const match = competitorMatches.find((candidate) => candidate.competitorId === competitor.id) ?? null;

        if (!match) {
          if (!includePriorCompetitors) {
            return null;
          }

          return {
            competitor,
            snapshot: null,
            label: undefined,
            noDataAvailable: true,
          };
        }

        if (!includePriorCompetitors && !match.isCurrentQuarter) {
          return null;
        }

        return {
          competitor,
          snapshot: match.snapshot ?? null,
          label: match.isCurrentQuarter ? 'Selected update' : 'Prior update',
          noDataAvailable: false,
        };
      })
    : [];

  const definedEntries = competitorEntries.filter((entry): entry is CompetitorEntry => entry !== null);

  return (
    <Container>
      {!definedEntries.length && <span style={{ color: 'var(--text-secondary)' }}>No data available</span>}
      {definedEntries.map(({ competitor, snapshot, label, noDataAvailable }) => (
        <SnapshotDetails
          key={snapshot?.id ? `${competitor.id}-${snapshot.id}` : `competitor-${competitor.id}`}
          snapshot={snapshot}
          avatarType="competitor"
          label={label}
          noDataAvailable={noDataAvailable}
          name={competitor?.comunity_name}
        />
      ))}
    </Container>
  );
};

export default CompetitorListSection;
